import { ElementTag } from "./ElementTag.js";
import { PlaywrightAction } from "./PlaywrightAction.js";
/**
 * Resolves an action type to the corresponding ElementTag
 *
 * @param action The action type (CLICK, FILL_IN, SELECT_DROPDOWN)
 * @returns The corresponding ElementTag
 */
export function resolveElementTag(action) {
    switch (action) {
        case PlaywrightAction.CLICK:
            return [ElementTag.CLICKABLE, ElementTag.FILLABLE];
        case PlaywrightAction.FILL_IN:
            return [ElementTag.FILLABLE];
        case PlaywrightAction.SELECT_DROPDOWN:
            return [ElementTag.SELECTABLE];
        case PlaywrightAction.HOVER:
            return [ElementTag.CLICKABLE, ElementTag.FILLABLE, ElementTag.NON_INTERACTIVE_ELEMENT];
        case PlaywrightAction.ASSERT_EXACT_VALUE:
        case PlaywrightAction.ASSERT_CONTAINS_VALUE:
        case PlaywrightAction.EXTRACT_VALUE:
        case PlaywrightAction.WAIT_FOR:
            return [ElementTag.CLICKABLE, ElementTag.FILLABLE, ElementTag.NON_INTERACTIVE_ELEMENT];
        case PlaywrightAction.WAIT_FOR_OTP:
        case PlaywrightAction.GEN_TOTP:
            return [ElementTag.FILLABLE];
        default:
            console.error(`Unknown action: ${action}`);
            throw new Error(`Unknown action: ${action}`);
    }
}
export function getElementText(element) {
    var _a;
    return element.textContent || element.innerText || element.value || ((_a = element.querySelector('input')) === null || _a === void 0 ? void 0 : _a.value) || '';
}
/**
 * Checks if any string in the array matches the given regular expression pattern.
 *
 * @param array - Array of strings to test.
 * @param pattern - Regular expression to test against each string.
 * @returns true if at least one string matches the pattern, false otherwise.
 */
function testArray(array, pattern) {
    return array.some(item => pattern.test(item));
}
/**
 * Determines if an element is fillable (can accept text input)
 *
 * @param element The DOM element to check
 * @returns boolean indicating if the element is fillable
 */
export function isFillableElement(element) {
    var _a;
    if (!element)
        return false;
    // Check if it's an input element
    if (element.tagName === 'INPUT') {
        const inputType = (_a = element.type) === null || _a === void 0 ? void 0 : _a.toLowerCase();
        const isOTPInput = element.getAttribute('data-input-otp') === 'true' ||
            element.getAttribute('autocomplete') === 'one-time-code';
        // if it's an OTP input, don't consider it fillable as it's handled by OTP logic
        if (isOTPInput)
            return false;
        // Include text, password, email, number, tel, url, search, date, datetime-local, month, week, time
        const fillableTypes = [
            'text', 'password', 'email', 'number', 'tel', 'url', 'search',
            'date', 'datetime-local', 'month', 'week', 'time'
        ];
        return fillableTypes.includes(inputType);
    }
    // Check if it's a textarea
    if (element.tagName === 'TEXTAREA') {
        return true;
    }
    // Check if it's a contenteditable element
    if (element.getAttribute('contenteditable') === 'true') {
        return true;
    }
    return false;
}
/**
 * Determines if an element is a selectable dropdown element
 *
 * @param element The DOM element to check
 * @returns boolean indicating if the element is a select element
 */
export function isSelectableElement(element) {
    return element && element.tagName === 'SELECT';
}
/**
 * Determines if an element is a non-interactive element (not clickable, fillable, or selectable)
 *
 * @param element The DOM element to check
 * @returns boolean indicating if the element is non-interactive
 */
export function isSliderElement(element) {
    if (!element)
        return false;
    // check for <input type="range">
    if (element.tagName === 'INPUT' && element.getAttribute('type') === 'range')
        return true;
    // check for role and additional attributes
    const sliderAttributes = ['aria-valuenow', 'aria-valuemin', 'aria-valuemax'];
    if (element.getAttribute('role') === 'slider' || sliderAttributes.some(attr => element.hasAttribute(attr)))
        return true;
    // check for class names
    const classNames = Array.from(element.classList);
    const sliderClassPattern = /^MuiSlider|mat-slider|mdl-slider|ui-slider|carousel/;
    if (testArray(classNames, sliderClassPattern))
        return true;
    return false;
}
/**
 * Determines if an element is an <input type="file"> element.
 *
 * @param element The DOM element to check
 * @returns boolean indicating if the element is an input file element
 */
export function isInputFileElement(element) {
    return element && element.tagName === 'INPUT' && element.getAttribute('type') === 'file';
}
/**
 * Determines if an element is clickable
 *
 * @param element The DOM element to check
 * @returns boolean indicating if the element is fillable
 */
export function isClickableElement(element) {
    if (!element)
        return false;
    let depth = 0;
    while (depth < 5 && element && element.nodeType === Node.ELEMENT_NODE) {
        if (isClickableElementHelper(element) === IsClickable.YES)
            return true;
        if (isClickableElementHelper(element) === IsClickable.NO)
            return false;
        // if maybe, continue searching up to 5 levels up the DOM tree
        element = element.parentNode;
        depth++;
    }
    return false;
}
// clickable element detection result
var IsClickable;
(function (IsClickable) {
    IsClickable["YES"] = "YES";
    IsClickable["NO"] = "NO";
    IsClickable["MAYBE"] = "MAYBE";
})(IsClickable || (IsClickable = {}));
function isClickableElementHelper(element) {
    var _a, _b;
    if (!element)
        return IsClickable.NO;
    //check for tag name
    const tagName = element.tagName.toLowerCase();
    const clickableTags = [
        'a', 'button',
    ];
    if (clickableTags.includes(tagName))
        return IsClickable.YES;
    //check for clickable <input>
    const inputType = (_a = element.type) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    const clickableTypes = [
        'button', 'submit', 'reset', 'checkbox', 'radio',
    ];
    const ariaAutocompleteValues = [
        'list', 'both',
    ];
    if (tagName === 'input') {
        if (clickableTypes.includes(inputType) || ariaAutocompleteValues.includes((_b = element.getAttribute('aria-autocomplete')) !== null && _b !== void 0 ? _b : ''))
            return IsClickable.YES;
        if (['date', 'number', 'range'].includes(inputType))
            return IsClickable.NO; //don't record the click as a change event will be generated for elements that generate an input change event
    }
    // check for cursor type
    const style = window.getComputedStyle(element);
    if (style.cursor === 'pointer')
        return IsClickable.YES;
    // check for attributes
    const clickableRoles = [
        'button', 'combobox', 'listbox', 'dropdown', 'option', 'menu', 'menuitem',
        'navigation', 'checkbox', 'switch', 'toggle', 'slider', 'textbox', 'listitem',
        'presentation',
    ];
    const ariaPopupValues = [
        'true', 'listbox', 'menu',
    ];
    if (element.hasAttribute('onclick') ||
        clickableRoles.includes(element.getAttribute('role') || '') ||
        ariaPopupValues.includes(element.getAttribute('aria-haspopup') || ''))
        return IsClickable.YES;
    // check for tabindex (means element is focusable and therefore clickable)
    if (parseInt(element.getAttribute('tabindex') || '-1') >= 0)
        return IsClickable.YES;
    // extract class names
    const classNames = Array.from(element.classList);
    // check for checkbox/radio-like class name - TODO: check if can be removed
    const checkboxPattern = /checkbox|switch|toggle|slider/i;
    if (testArray(classNames, checkboxPattern))
        return IsClickable.YES;
    // check for Material UI class names
    const muiClickableClassPattern = /MuiButton|MuiIconButton|MuiChip|MuiMenuItem|MuiListItem|MuiInputBase|MuiOutlinedInput|MuiSelect|MuiAutocomplete|MuiToggleButton|MuiBackdrop-root|MuiBackdrop-invisible/;
    if (testArray(classNames, muiClickableClassPattern))
        return IsClickable.YES;
    // check for SalesForce class names
    const sfClassPattern = /slds-button|slds-dropdown|slds-combobox|slds-picklist|slds-tabs|slds-pill|slds-action|slds-row-action|slds-context-bar|slds-input|slds-rich-text-area|slds-radio|slds-checkbox|slds-toggle|slds-link|slds-accordion|slds-tree/;
    if (testArray(classNames, sfClassPattern))
        return IsClickable.YES;
    // check for chart dots
    const chartClickableClassPattern = /recharts-dot/;
    if (testArray(classNames, chartClickableClassPattern))
        return IsClickable.YES;
    // check for React component classes
    const reactClickableClassPattern = /react-select|ant-select|rc-select|react-dropdown|react-autocomplete|react-datepicker|react-modal|react-tooltip|react-popover|react-menu|react-tabs|react-accordion|react-collapse|react-toggle|react-switch|react-checkbox|react-radio|react-button|react-link|react-card|react-list-item|react-menu-item|react-option|react-tab|react-panel|react-drawer|react-sidebar|react-nav|react-breadcrumb|react-pagination|react-stepper|react-wizard|react-carousel|react-slider|react-range|react-progress|react-badge|react-chip|react-tag|react-avatar|react-icon|react-fab|react-speed-dial|react-floating|react-sticky|react-affix|react-backdrop|react-overlay|react-portal|react-transition|react-animate|react-spring|react-framer|react-gesture|react-drag|react-drop|react-sortable|react-resizable|react-split|react-grid|react-table|react-datagrid|react-tree|react-treeview|react-file|react-upload|react-cropper|react-image|react-gallery|react-lightbox|react-player|react-video|react-audio|react-chart|react-graph|react-diagram|react-flow|react-d3|react-plotly|react-vega|react-vis|react-nivo|react-recharts|react-victory|react-echarts|react-highcharts|react-google-charts|react-fusioncharts|react-apexcharts|react-chartjs|react-chartkick|react-sparklines|react-trend|react-smooth|react-animated|react-lottie|react-spring|react-framer-motion|react-pose|react-motion|react-transition-group|react-router|react-navigation/i;
    if (testArray(classNames, reactClickableClassPattern))
        return IsClickable.YES;
    //check for cloudinary class names
    const cloudinaryClickableClassPattern = /cld-combobox|cld-upload-button|cld-controls|cld-player|cld-tab|cld-menu-item|cld-close|cld-play|cld-pause|cld-fullscreen|cld-browse|cld-cancel|cld-retry/;
    if (testArray(classNames, cloudinaryClickableClassPattern))
        return IsClickable.YES;
    return IsClickable.MAYBE;
}
export function isTextInputElement(element) {
    return (element.tagName === 'INPUT' && element.getAttribute('type') !== 'checkbox' && element.getAttribute('type') !== 'radio') ||
        element.tagName === 'TEXTAREA' ||
        element.getAttribute('contenteditable') === 'true' ||
        element.getAttribute('role') === 'textbox';
}
export function getParentNode(element) {
    if (!element || element.nodeType !== Node.ELEMENT_NODE)
        return null;
    let parent = null;
    // SF is using slots and shadow DOM heavily
    // However, there might be slots in the light DOM which shouldn't be traversed
    if (element.assignedSlot && element.getRootNode() instanceof ShadowRoot)
        parent = element.assignedSlot;
    else
        parent = element.parentNode;
    // Check if we're at a shadow root
    if (parent && parent.nodeType !== Node.ELEMENT_NODE && parent.getRootNode() instanceof ShadowRoot)
        parent = parent.getRootNode().host;
    return parent;
}
export function getElementDepth(element) {
    let depth = 0;
    let currentElement = element;
    while ((currentElement === null || currentElement === void 0 ? void 0 : currentElement.nodeType) === Node.ELEMENT_NODE) {
        depth++;
        currentElement = getParentNode(currentElement);
    }
    return depth;
}
//# sourceMappingURL=element-utils.js.map